import { useMergedRef, useResizeObserver } from '@mantine/hooks';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { dynamicImageUrl, isObjectUrl } from '@_/utils-frontend';
import { Skeleton, SkeletonBlock } from '../Skeleton/index.ts';

export type ImageProps = React.ComponentPropsWithRef<'img'> & {
  fit?: 'contain' | 'cover' | 'none';
};

export function Image({ fit = 'none', ref: forwardedRef, src, ...props }: ImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);

  const oldSrcRef = useRef(src);
  const srcSetRef = useRef<string[] | undefined>(undefined);

  const [sizeRef, { width, height }] = useResizeObserver();
  const imgRef = useRef<HTMLImageElement | null>(null);
  const ref = useMergedRef(imgRef, sizeRef, forwardedRef);

  if (oldSrcRef.current !== src) {
    oldSrcRef.current = src;
    srcSetRef.current = undefined;
  }

  const srcSet = useMemo(() => {
    if (!src || (!width && !height)) {
      return undefined;
    }

    if (isObjectUrl(src)) {
      return [src];
    }

    // Ensure image is only fetched once even when resized
    if (srcSetRef.current) {
      return srcSetRef.current;
    }

    srcSetRef.current = [1, 2, 3].map(dpr =>
      dynamicImageUrl(src, {
        format: 'jpeg',
        contain: fit !== 'cover',
        width: width ? Math.ceil(width) * dpr : undefined,
        height: height ? Math.ceil(height) * dpr : undefined
      })
    );

    return [...srcSetRef.current];
  }, [fit, height, src, width]);

  useEffect(() => {
    setIsLoaded(false);

    if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [src]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const imageProps: React.ComponentPropsWithoutRef<'img'> = {
    ...props,
    onLoad: handleLoad,
    srcSet: srcSet?.map((url, index) => `${url} ${index + 1}x`).join(', ')
  };

  if (fit === 'cover') {
    return (
      <div
        ref={sizeRef}
        className={clsx(
          props.className,
          !!props.className &&
            !['absolute', 'fixed', 'sticky'].includes(props.className) &&
            'relative',
          'overflow-hidden'
        )}
      >
        <img
          {...imageProps}
          ref={ref}
          className={clsx('block size-full object-cover object-center', !isLoaded && 'opacity-0')}
        />

        {!isLoaded && (
          <Skeleton className="absolute inset-0">
            <SkeletonBlock className="size-full rounded-none" />
          </Skeleton>
        )}
      </div>
    );
  }

  return (
    <img
      {...imageProps}
      ref={ref}
      className={clsx('block object-contain object-center', props.className)}
    />
  );
}
